// ---------------------------------------------------------------------------
// Keeping an active till signed in.
//
// THE BUG THIS EXISTS FOR. The access token lasts fifteen minutes and was only
// ever refreshed REACTIVELY - the till made a request, got a 401, refreshed,
// and replayed. That works when it works, but it meant every quiet quarter of
// an hour ended with a failed request, and any hiccup during that one refresh
// put a cashier on the login page with a seven-day refresh token sitting valid
// in a cookie.
//
// So the session is now topped up BEFORE it expires, on a timer, while
// somebody is actually using the till. A refresh that happens with eleven
// minutes still on the clock has four minutes of retries available to it and
// nobody notices any of them.
//
// WHAT "ACTIVE" MEANS. Real interaction - a pointer, a key, a scroll, a
// navigation. Not a background timer, and not the health probe: a till left
// switched on overnight in an empty shop should NOT hold a session open until
// morning, which is what a refresh loop with no activity check would do.
//
// The activity stamp is debounced hard. A cashier weighing meat generates
// hundreds of pointer events a minute and every one of them writing to
// `localStorage` would be hundreds of synchronous disk writes for a number
// that only needs to be right to the minute.
// ---------------------------------------------------------------------------

import { api, ApiError } from './apiClient';

/// How long the server's access token lasts. Kept in step with
/// `ACCESS_TOKEN_MINUTES`, whose default is fifteen.
const ACCESS_TOKEN_MS = 15 * 60_000;

/// Refresh at a third of the token's life, so two consecutive failures still
/// leave time for a third attempt before anything expires.
const REFRESH_EVERY_MS = Math.round(ACCESS_TOKEN_MS / 3);

/// How long a till may sit untouched before its session is allowed to lapse.
///
/// Deliberately generous. This is a butcher's shop, not a bank terminal: a
/// cashier steps out to the cold room, serves somebody at the door, or takes
/// a delivery. Eight hours covers a shift; the refresh token's own seven-day
/// life is the real backstop, and closing the shift or signing out ends the
/// session properly whatever this says.
const DEFAULT_IDLE_LIMIT_MS = 8 * 60 * 60_000;

/// At most one activity stamp a minute, however much the cashier is doing.
const ACTIVITY_DEBOUNCE_MS = 60_000;

const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'visibilitychange',
] as const;

let refreshTimer: ReturnType<typeof setInterval> | null = null;
let lastActivityAt = Date.now();
let lastStampedAt = 0;
let attached = false;
let idleLimitMs = DEFAULT_IDLE_LIMIT_MS;

/// Called when the till has genuinely been left alone for the limit.
let onIdleTimeout: (() => void) | null = null;

/// A refresh already in flight. Two screens noticing the timer at once must
/// not produce two refreshes, which would rotate the token twice and could
/// invalidate the first one's answer.
let inFlight: Promise<boolean> | null = null;

/// Anything the cashier does that counts as being at the till.
export function markActivity(): void {
  lastActivityAt = Date.now();
}

function onInteraction(): void {
  const now = Date.now();
  lastActivityAt = now;

  // Debounced: hundreds of pointer events a minute must not become hundreds
  // of writes for a number that only has to be right to the minute.
  if (now - lastStampedAt < ACTIVITY_DEBOUNCE_MS) return;
  lastStampedAt = now;
}

/// True when somebody has touched the till inside the idle limit.
export function isActive(): boolean {
  return Date.now() - lastActivityAt < idleLimitMs;
}

/// Top the session up. Shared, so concurrent callers make one request.
export async function refreshSession(): Promise<boolean> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      await api.post('/auth/refresh', {});
      return true;
    } catch (error) {
      // A REJECTED refresh ends the session, and the API client's own handler
      // deals with that. A refresh that never arrived says nothing about the
      // session, and is simply tried again on the next tick.
      if (error instanceof ApiError && (error.code === 'NETWORK_ERROR' || error.status >= 500)) {
        return false;
      }
      return false;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/// Start keeping the session alive.
///
/// Safe to call more than once; only the first call attaches anything.
export function startSessionKeeper(options: { idleLimitMs?: number; onIdle?: () => void } = {}): void {
  if (attached || typeof window === 'undefined') return;
  attached = true;

  if (options.idleLimitMs && options.idleLimitMs > 0) idleLimitMs = options.idleLimitMs;
  onIdleTimeout = options.onIdle ?? null;

  lastActivityAt = Date.now();

  for (const event of ACTIVITY_EVENTS) {
    window.addEventListener(event, onInteraction, { passive: true });
  }

  refreshTimer = setInterval(() => {
    // Untouched for the whole limit: let it lapse rather than holding a
    // session open on a till nobody is standing at.
    if (!isActive()) {
      onIdleTimeout?.();
      return;
    }

    void refreshSession();
  }, REFRESH_EVERY_MS);
}

export function stopSessionKeeper(): void {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = null;

  if (typeof window !== 'undefined') {
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, onInteraction);
    }
  }

  attached = false;
  onIdleTimeout = null;
  inFlight = null;
}

/// Only for tests.
export function resetSessionKeeperForTests(): void {
  stopSessionKeeper();
  lastActivityAt = Date.now();
  lastStampedAt = 0;
  idleLimitMs = DEFAULT_IDLE_LIMIT_MS;
}

/// Exposed so tests and screens can reason about the same numbers rather than
/// repeating them.
export const SESSION_TIMINGS = {
  ACCESS_TOKEN_MS,
  REFRESH_EVERY_MS,
  DEFAULT_IDLE_LIMIT_MS,
  ACTIVITY_DEBOUNCE_MS,
} as const;
